import { Fragment, ReactElement, useEffect, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "./icons";
import { classNames } from "../utils";
import ReactTooltip from "react-tooltip";
import LoadingSpinner from "./LoadingSpinner";

export type DropdownItem = { id: string; label: string };
type Props = {
  items: DropdownItem[];
  onChange?: (selected: DropdownItem) => void;
  name: string;
  label?: string;
  initial?: DropdownItem;
  labelClassName?: string;
  buttonContainerClassName?: string;
  containerClassName?: string;
  placeHolder?: string;
  disabled?: boolean;
  isValid?: boolean;
  icon?: ReactElement | null;
  tooltip?: string | null;
  html?: boolean;
  isLoading?: boolean;
};

export default function DropdownComboSimple({
  items,
  onChange,
  label,
  name,
  initial,
  labelClassName,
  buttonContainerClassName,
  containerClassName,
  placeHolder,
  disabled = false,
  isValid = true,
  icon = null,
  tooltip = null,
  html = false,
  isLoading = false,
}: Props) {
  const [selected, setSelected] = useState<DropdownItem>();

  useEffect(() => {
    if (initial) {
      setSelected(initial);
    }
  }, [initial]);

  function handleChange(selectedItem: DropdownItem) {
    setSelected(selectedItem);
    onChange?.(selectedItem);
  }

  return (
    <div className={classNames("relative", containerClassName ?? "w-full")}>
      <div className="flex gap-1">
        {label && (
          <label
            htmlFor={name}
            className={classNames(
              "block mb-2 text-sm font-medium text-gray-600",
              labelClassName ?? ""
            )}
          >
            {label}
            {isLoading && <LoadingSpinner />}
          </label>
        )}
        {tooltip && icon && (
          <>
            <span data-tip={tooltip} data-for={name}>
              {icon}
            </span>
            <ReactTooltip
              className="hover:visible:opacity-1 w-96 mr-auto"
              delayHide={1000}
              effect="solid"
              html={html}
              id={name}
            />
          </>
        )}
      </div>
      {!isLoading && (
        <Combobox
          disabled={disabled}
          value={selected ?? ""}
          onChange={handleChange}
        >
          <div>
            <div
              className={`${buttonContainerClassName} relative w-full p-2 pl-1 cursor-default overflow-hidden rounded-lg border ${
                isValid ? "" : "border-rose-600"
              }  bg-white text-left shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-300 sm:text-sm`}
            >
              <Combobox.Button className="relative inset-y-0 right-0 flex justify-between px-2 w-full items-center">
                <span className="font-medium text-gray-600 w-30">
                  {selected?.label}
                </span>
                {!selected ? (
                  <span className="flex-grow text-left text-gray-400">
                    {placeHolder}
                  </span>
                ) : null}
                {!disabled ? <ChevronDownIcon aria-hidden="true" /> : null}
              </Combobox.Button>
            </div>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50">
                {items.map((item) => (
                  <Combobox.Option
                    key={item.id}
                    className={({ active }) =>
                      `flex cursor-pointer select-none py-2 pl-10 pr-4 ${
                        active ? "bg-gray-600 text-white" : "text-gray-900"
                      }`
                    }
                    value={item}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={`block truncate ${
                            selected ? "font-medium" : "font-normal"
                          }`}
                        >
                          {item.label}
                        </span>
                        {selected && (
                          <span
                            className={`inset-y-0 left-0 flex items-center pl-3 ${
                              active ? "text-white" : "text-gray-600"
                            }`}
                          >
                            <CheckIcon aria-hidden="true" />
                          </span>
                        )}
                      </>
                    )}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            </Transition>
          </div>
        </Combobox>
      )}
    </div>
  );
}
